import { useState, useEffect } from 'react';
import { useWebSocket } from './WebSocketContext';

const folderRestrictionsCache = new Map();
const folderRestrictionsInFlight = new Map();

/**
 * Hook to fetch and manage folder restriction settings for a case
 * Returns restriction state that can be passed to useFolderPicker
 */
export function useFolderRestrictions(caseid) {
  const { ws, subscribeToMessages } = useWebSocket();
  const cached = caseid ? folderRestrictionsCache.get(caseid) : null;
  const [restrictToUserFolder, setRestrictToUserFolder] = useState(cached?.restrictToUserFolder || false);
  const [allowedRoots, setAllowedRoots] = useState(cached?.allowedRoots || []);
  const [displayRoot, setDisplayRoot] = useState(cached?.displayRoot || '');
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (!ws || !caseid) {
      setLoading(false);
      return;
    }

    // Subscribe to getFoldersSettings response
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      if (parsedMessage.command === 'getFoldersSettings') {
        const data = parsedMessage.data || {};
        setRestrictToUserFolder(data.restrictToUserFolder || false);
        setAllowedRoots(data.allowedRoots || []);
        setDisplayRoot(data.displayRoot || '');
        setLoading(false);
        folderRestrictionsCache.set(caseid, {
          restrictToUserFolder: data.restrictToUserFolder || false,
          allowedRoots: data.allowedRoots || [],
          displayRoot: data.displayRoot || ''
        });
        folderRestrictionsInFlight.delete(caseid);
      }
    });

    // Request folder settings
    if (!folderRestrictionsInFlight.get(caseid)) {
      folderRestrictionsInFlight.set(caseid, true);
      const message = {
        command: 'getFoldersSettings',
        caseid: caseid
      };
      ws.send(JSON.stringify(message));
    }

    return unsubscribe;
  }, [ws, caseid, subscribeToMessages]);

  return {
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    loading
  };
}
